import { UUID, type ConsentStatus } from './measurementCore';
export type { ConsentStatus } from './measurementCore';
const CONSENT_KEY = 'parables:analytics-consent:v1';
const CONSENT_TIME_KEY = 'parables:analytics-consent-updated-at:v1';
const TRACKING_KEY = 'parables:tracking:uid';
const NOTICE_KEY = 'parables:consent-notices:v1';
const listeners = new Set<() => void>();
let memoryUid: string | undefined;
// The web funnel runs measurement by default and has no consent prompt.
// "granted" is the existing API's tracking-enabled value, not evidence of a
// visitor clicking Accept. Preserve any explicitly saved opt-out.
function readTrackingState(): {status: ConsentStatus; updatedAt?: string} {
  let saved: string | null = null;
  let updatedAt: string | undefined;
  try {
    saved = localStorage.getItem(CONSENT_KEY);
    const value = localStorage.getItem(CONSENT_TIME_KEY);
    if (value && Number.isFinite(Date.parse(value))) updatedAt = value;
  } catch { /* Tracking still works in memory when storage is unavailable. */ }
  const status = saved === 'denied' ? 'denied' : 'granted';
  if (status === 'granted' && (saved !== 'granted' || !updatedAt)) {
    updatedAt = new Date(Math.max(Date.now(), updatedAt ? Date.parse(updatedAt) + 1 : 0)).toISOString();
    try {
      localStorage.setItem(CONSENT_TIME_KEY, updatedAt);
      localStorage.setItem(CONSENT_KEY, status);
    } catch { /* Keep the enabled state and timestamp for this visit. */ }
  }
  return {status, updatedAt};
}
let {status: current, updatedAt: currentUpdatedAt} = readTrackingState();
export const getConsentUpdatedAt = () => currentUpdatedAt;
type ConsentNotice = {consentStatus: ConsentStatus; consentUpdatedAt: string};
let apiBaseUrl = '';
let notifying = false;
const productionHost = ['readparables.com', 'www.readparables.com', 'start.readparables.com', 'lander.readparables.com'].includes(location.hostname);
export const measurementEnvironment = import.meta.env.MODE === 'test' ? 'test' : import.meta.env.DEV ? 'development' : import.meta.env.MODE === 'preview' || !productionHost ? 'preview' : 'production';
export const measurementIsTest = measurementEnvironment !== 'production' || ['localhost', '127.0.0.1'].includes(location.hostname) || import.meta.env.VITE_ANALYTICS_TEST === 'true';
export const getConsentStatus = (): ConsentStatus => current;
export const canMeasure = (): boolean => current === 'granted';
export const canMeasureFunnel = canMeasure;
// Test and preview journeys may reach product analytics, never ad delivery.
export const canMeasureMarketing = (): boolean => canMeasure() && !measurementIsTest;
export function subscribeConsent(listener: () => void): () => void { listeners.add(listener); return () => { listeners.delete(listener); }; }
export function getTrackingUuid(): string {
  if (memoryUid) return memoryUid;
  if (canMeasureFunnel()) { try { const existing = localStorage.getItem(TRACKING_KEY); if (existing && UUID.test(existing)) return memoryUid = existing; } catch { /* Storage optional. */ } }
  memoryUid = crypto.randomUUID();
  if (canMeasureFunnel()) { try { localStorage.setItem(TRACKING_KEY, memoryUid); } catch { /* Storage optional. */ } }
  return memoryUid;
}
function purgeOptionalStorage(): void {
  for (const storage of [localStorage, sessionStorage]) {
    for (const key of Object.keys(storage)) {
      if (canMeasureFunnel() && /^(parables:tracking:(?:uid$|pending:)|parables:slim:analytics:)/.test(key)) continue;
      if (/^(ph_|dl_|__dl_|parables:(?:tracking:pending|tracking:uid|measurement:|watch:analytics|slim:analytics|tracked-|pending-checkout))/.test(key)) storage.removeItem(key);
    }
  }
  for (const cookie of document.cookie.split(';')) {
    const name = cookie.split('=')[0].trim();
    if (!/^(ph_|_fbp$|_fbc$|__dl_)/.test(name)) continue;
    for (const domain of ['', `; domain=${location.hostname}`, '; domain=.readparables.com']) document.cookie = `${name}=; path=/; max-age=0${domain}; SameSite=Lax`;
  }
}
async function flushNotices(): Promise<void> {
  if (!apiBaseUrl || notifying || !navigator.onLine) return;
  notifying = true;
  try {
    const notices = JSON.parse(localStorage.getItem(NOTICE_KEY) ?? '{}') as Record<string, ConsentNotice>;
    for (const [trackingUuid, notice] of Object.entries(notices)) {
      const {consentStatus, consentUpdatedAt} = notice;
      if (!UUID.test(trackingUuid) || !['granted', 'denied'].includes(consentStatus) || !Number.isFinite(Date.parse(consentUpdatedAt))) continue;
      const response = await fetch(`${apiBaseUrl}/api/analytics/consent`, { method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({ trackingUuid, consentStatus, consentUpdatedAt }), keepalive: true, signal: AbortSignal.timeout(10000) });
      if (!response.ok) break;
      const latest = JSON.parse(localStorage.getItem(NOTICE_KEY) ?? '{}') as Record<string, ConsentNotice>;
      if (latest[trackingUuid]?.consentStatus === consentStatus && latest[trackingUuid]?.consentUpdatedAt === consentUpdatedAt) delete latest[trackingUuid];
      localStorage.setItem(NOTICE_KEY, JSON.stringify(latest));
    }
  } catch { /* A necessary preference notice remains for next load/online. */ }
  finally { notifying = false; try { if (localStorage.getItem(NOTICE_KEY) && localStorage.getItem(NOTICE_KEY) !== '{}') window.setTimeout(() => void flushNotices(), 5000); } catch { /* Storage optional. */ } }
}
export function configureConsentEndpoint(base: string): void {
  apiBaseUrl = base.replace(/\/$/, '');
  // Synchronize the enabled default for returning visitors whose backend
  // checkout context was previously recorded as unknown.
  if (current === 'granted' && currentUpdatedAt) {
    try {
      const notices = JSON.parse(localStorage.getItem(NOTICE_KEY) ?? '{}');
      const uid = getTrackingUuid();
      const pending = notices[uid] as ConsentNotice | undefined;
      if (!pending || Date.parse(pending.consentUpdatedAt) <= Date.parse(currentUpdatedAt)) {
        notices[uid] = {consentStatus: current, consentUpdatedAt: currentUpdatedAt};
        localStorage.setItem(NOTICE_KEY, JSON.stringify(notices));
      }
    } catch { /* Checkout also carries the state when storage is unavailable. */ }
  }
  void flushNotices();
}
export function setConsentStatus(next: Exclude<ConsentStatus, 'unknown'>): void {
  const previous = current;
  const uid = memoryUid ?? (() => { try { return localStorage.getItem(TRACKING_KEY) ?? undefined; } catch { return; } })();
  current = next;
  currentUpdatedAt = new Date(Math.max(Date.now(), (currentUpdatedAt ? Date.parse(currentUpdatedAt) : 0) + 1)).toISOString();
  try { localStorage.setItem(CONSENT_KEY, next); localStorage.setItem(CONSENT_TIME_KEY, currentUpdatedAt); } catch { /* Session choice still applies. */ }
  if (uid && UUID.test(uid)) {
    try { const notices = JSON.parse(localStorage.getItem(NOTICE_KEY) ?? '{}'); notices[uid] = {consentStatus: next, consentUpdatedAt: currentUpdatedAt}; localStorage.setItem(NOTICE_KEY, JSON.stringify(notices)); } catch { /* Best effort when storage blocked. */ }
  }
  // Notify collectors before deleting their persisted queues and identifiers.
  for (const listener of listeners) { try { listener(); } catch { /* Checkout cannot depend on a vendor. */ } }
  if (next === 'denied') { try { purgeOptionalStorage(); } catch { /* Browser can deny storage. */ } memoryUid = undefined; }
  else if (previous !== 'granted' && memoryUid) { try { localStorage.setItem(TRACKING_KEY, memoryUid); } catch { /* Optional. */ } }
  void flushNotices();
}
window.addEventListener('online', () => void flushNotices());
window.addEventListener('storage', event => { if (event.key === CONSENT_KEY) { ({status: current, updatedAt: currentUpdatedAt} = readTrackingState()); for (const listener of listeners) listener(); if (!canMeasure()) { try { purgeOptionalStorage(); } catch { /* Optional. */ } memoryUid = undefined; } } });
// A saved opt-out still clears queued measurement and attribution.
if (!canMeasure()) { try { purgeOptionalStorage(); } catch { /* Optional. */ } }
